import { useTranslation } from '@portals-platform/i18n';
import { Button, EmptyState, Skeleton } from '@portals-platform/ui';
import { ArrowLeftIcon, LockIcon } from 'lucide-react';
import { isCertificateEditable, useCertificate } from '../../data-access/index.js';
import { CertificateLoadError } from '../certificate-details/certificate-load-error.js';
import { CertificateForm } from './certificate-form.js';

interface CertificateEditProps {
  certificateId: string;
  onBack: () => void;
}

export function CertificateEdit({ certificateId, onBack }: CertificateEditProps) {
  const { t } = useTranslation();
  const { certificate, isLoading, isError, error } = useCertificate(certificateId);

  if (isLoading) {
    return <EditSkeleton />;
  }

  if (isError || !certificate) {
    return <CertificateLoadError error={error} onBack={onBack} />;
  }

  if (!isCertificateEditable(certificate)) {
    return (
      <EmptyState
        icon={<LockIcon />}
        title={t('certificates.edit.notEditable.title')}
        description={t('certificates.edit.notEditable.description')}
        action={
          <Button variant="outline" onClick={onBack}>
            <ArrowLeftIcon aria-hidden="true" />
            {t('certificates.edit.backToDetails')}
          </Button>
        }
      />
    );
  }

  return <CertificateForm key={certificate.id} initialCertificate={certificate} />;
}

function EditSkeleton() {
  return (
    <div className="flex flex-col gap-6" aria-busy="true">
      <Skeleton className="h-40 w-full" />
      <Skeleton className="h-40 w-full" />
    </div>
  );
}
